import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BoardMember } from '../../types';
import { boardService, uploadService } from '../../services/firebaseService';

const initialForm: BoardMember = {
  name: '',
  role: '',
  bio: '',
  order: undefined,
  photoUrl: ''
};

// Convert Google Drive URL to direct image URL
const convertGoogleDriveUrl = (url: string): string => {
  if (!url) return url;
  
  // If it's already in the correct format, return as-is
  if (url.includes('drive.google.com/uc?export=view&id=')) {
    return url;
  }
  
  // Extract file ID from various Google Drive URL formats:
  let fileId: string | null = null;
  
  // Format 1: https://drive.google.com/file/d/FILE_ID/view
  const fileDMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileDMatch) {
    fileId = fileDMatch[1];
  }
  
  // Format 2: https://drive.google.com/uc?export=view&id=FILE_ID
  // Format 3: https://drive.google.com/open?id=FILE_ID
  if (!fileId) {
    const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (idMatch) {
      fileId = idMatch[1];
    }
  }
  
  // Convert to direct view URL
  if (fileId) {
    return `https://drive.google.com/uc?export=view&id=${fileId}`;
  }
  
  return url;
};

const BoardManager: React.FC = () => {
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [form, setForm] = useState<BoardMember>({ ...initialForm });
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const list = await boardService.getBoardMembers();
      setMembers(list);
    } catch (err) {
      setError('Failed to load board members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'order' ? (value ? Number(value) : undefined) : value }));
    
    // Update image preview when photoUrl changes
    if (name === 'photoUrl' && value) {
      const convertedUrl = convertGoogleDriveUrl(value);
      setImagePreview(convertedUrl);
    } else if (name === 'photoUrl' && !value) {
      setImagePreview(null);
    }
  };

  const handleEdit = (member: BoardMember) => {
    if (!member.id) return;
    setEditingMemberId(member.id);
    setForm({
      name: member.name,
      role: member.role,
      bio: member.bio || '',
      order: member.order,
      photoUrl: member.photoUrl || ''
    });
    setPhotoFile(null);
    setImagePreview(member.photoUrl ? convertGoogleDriveUrl(member.photoUrl) : null);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setForm({ ...initialForm });
    setPhotoFile(null);
    setImagePreview(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.role) {
      setError('Name and role are required');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      let photoUrl = form.photoUrl?.trim() || '';
      
      // Convert Google Drive URL if present
      if (photoUrl && !photoFile) {
        photoUrl = convertGoogleDriveUrl(photoUrl);
      }
      
      if (photoFile) {
        try {
          setUploadProgress(0);
          const path = `board-photos/${Date.now()}_${photoFile.name}`;
          photoUrl = await uploadService.uploadFile(photoFile, path, (progress) => {
            setUploadProgress(progress);
          });
          setUploadProgress(null);
        } catch (e: any) {
          console.error('Photo upload failed:', e);
          setUploadProgress(null);
          setError(`Photo upload failed: ${e?.message || 'unknown error'}`);
          setSubmitting(false);
          return;
        }
      }

      const memberData = {
        name: form.name.trim(),
        role: form.role.trim(),
        bio: form.bio?.trim() || '',
        order: typeof form.order === 'number' ? form.order : undefined,
        photoUrl
      };

      if (editingMemberId) {
        // Update existing member
        await boardService.updateBoardMember(editingMemberId, memberData);
      } else {
        // Add new member
        await boardService.addBoardMember(memberData);
      }

      handleCancelEdit();
      await loadMembers();
    } catch (err: any) {
      console.error(`Failed to ${editingMemberId ? 'update' : 'add'} board member:`, err);
      setError(`Failed to ${editingMemberId ? 'update' : 'add'} member: ${err?.message || 'permission denied or network error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    if (!confirm('Remove this board member?')) return;
    try {
      await boardService.deleteBoardMember(id);
      await loadMembers();
    } catch (err) {
      setError('Failed to delete member');
    }
  };

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Board Manager</h1>
        {error && <div className="alert error">{error}</div>}
        <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '2rem' }}>
          {editingMemberId && (
            <div className="alert" style={{ backgroundColor: '#e3f2fd', color: '#1976d2', marginBottom: '1rem', padding: '0.75rem', borderRadius: '4px' }}>
              <strong>Editing:</strong> {form.name || 'Board Member'}
            </div>
          )}
          <div className="grid grid-2">
            <div>
              <label>Name</label>
              <input name="name" value={form.name} onChange={handleChange} placeholder="Full name" />
            </div>
            <div>
              <label>Role / Title</label>
              <input name="role" value={form.role} onChange={handleChange} placeholder="e.g., Board Member" />
            </div>
            <div>
              <label>Order (optional)</label>
              <input name="order" value={form.order ?? ''} onChange={handleChange} placeholder="1, 2, 3..." />
            </div>
            <div>
              <label>Photo URL (optional)</label>
              <input 
                name="photoUrl" 
                value={form.photoUrl ?? ''} 
                onChange={handleChange} 
                placeholder="https://... (Google Drive URLs supported)" 
              />
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                Note: Google Drive images must be shared publicly (Anyone with the link can view) to display properly.
              </p>
              {imagePreview && (
                <div style={{ marginTop: '0.5rem' }}>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Preview:</p>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      setError('Image failed to load. Please check the URL or try a different image.');
                    }}
                    onLoad={() => setError(null)}
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px', 
                      borderRadius: '8px', 
                      border: '1px solid #ddd',
                      objectFit: 'cover'
                    }} 
                  />
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <label>Upload Photo (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
            {photoFile && <p style={{ fontSize: '0.9rem' }}>Selected: {photoFile.name}</p>}
          </div>
          <div>
            <label>Bio (optional)</label>
            <textarea 
              name="bio" 
              value={form.bio ?? ''} 
              onChange={handleChange} 
              placeholder="Short bio" 
              rows={6}
              style={{
                whiteSpace: 'pre-wrap',
                resize: 'vertical'
              }}
            />
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
              Tip: Press Enter to create new paragraphs. Line breaks will be preserved.
            </p>
          </div>
          {uploadProgress !== null && (
            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Uploading image: {uploadProgress.toFixed(0)}%
              </p>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#e0e0e0', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${uploadProgress}%`, 
                  height: '100%', 
                  backgroundColor: '#007bff',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            {editingMemberId && (
              <button type="button" onClick={handleCancelEdit} disabled={submitting || uploadProgress !== null}>
                Cancel
              </button>
            )}
            <button type="submit" disabled={submitting || uploadProgress !== null}>
              {uploadProgress !== null 
                ? `Uploading... ${uploadProgress.toFixed(0)}%` 
                : submitting 
                  ? (editingMemberId ? 'Updating...' : 'Adding...')
                  : (editingMemberId ? 'Update Member' : 'Add Member')}
            </button>
          </div>
        </form>

        <h2>Current Members</h2>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="grid">
            {members.map((m) => (
              <div key={m.id} className="card">
                {m.photoUrl && (
                  <img src={m.photoUrl} alt={m.name} style={{ width: '100%', borderRadius: '8px', marginBottom: '0.5rem' }} />
                )}
                <h3 style={{ marginBottom: '0.25rem' }}>{m.name}</h3>
                <p style={{ color: 'var(--text-dark)', marginBottom: '0.5rem' }}>{m.role}</p>
                {typeof m.order === 'number' && <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>Order: {m.order}</p>}
                {m.bio && (
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {m.bio.split('\n').filter(para => para.trim()).map((paragraph, index) => (
                      <p key={index} style={{ marginBottom: '0.25rem' }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(m)}>Edit</button>
                  <button className="danger" onClick={() => handleDelete(m.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardManager;
